import Vue from 'vue';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';

// Production assets are shared by deployments. Decide at runtime so a preview
// never reports browser events to the live project, regardless of build settings.
const previewDeployment = document.documentElement.dataset.payboyPreview === 'true';

// Initialize Sentry with proper error handling and v6 compatibility
try {
  Sentry.init({
    dsn: process.env.NODE_ENV === 'production' && !previewDeployment ? 'https://65be6d2c0d3b438aad628fc901f08211@sentry.io/184921' : false,
    integrations: [
      new Integrations.Vue({
        Vue,
        attachProps: true,
        logErrors: true,
      }),
    ],
    // Not JSON.stringify'd: that wrapped the value in literal quotes, so events
    // were tagged `"staging"` / `"production"` and did not match Sentry's
    // environment filter.
    environment: process.env.SENTRY_ENVIRONMENT || null,
    serverName: process.env.HOSTNAME || null,
    // Third-party noise we cannot fix from app code:
    // - zaloJSV2 is the Zalo chat widget's global, injected by users'
    //   extensions / in-app browsers; the script does not exist in this repo.
    // - The ResizeObserver loop messages are the browser's benign
    //   back-pressure advisory (notifications deferred to the next frame).
    ignoreErrors: [
      /zaloJSV2/,
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
    ],
    // Intercom's messenger iframe scripts error internally when the iframe is
    // torn down mid-poll during a turbolinks:visit; not fixable from our code.
    denyUrls: [
      /frame-modern\.[a-f0-9]+\.js/,
      /js\.intercomcdn\.com/,
      /widget\.intercom\.io/,
    ],
    // Add performance monitoring
    tracesSampleRate: 0.5,
    // Add beforeSend hook to filter out known issues
    beforeSend(event, hint) {
      // Filter out NS_ERROR_NOT_INITIALIZED errors that are browser-specific
      if (event.exception) {
        const error = event.exception.values[0];
        if (error.type === 'NS_ERROR_NOT_INITIALIZED') {
          console.warn('Filtered out NS_ERROR_NOT_INITIALIZED error:', error);
          return null;
        }
      }
      return event;
    },
  });
} catch (error) {
  console.error('Sentry initialization failed:', error);
}

// Set user context on page load with error handling
document.addEventListener('turbolinks:load', () => {
  try {
    if (document.body && document.body.dataset) {
      Sentry.setUser(Object.assign({}, document.body.dataset));
    }
  } catch (error) {
    console.error('Failed to set Sentry user context:', error);
  }
});
